/** Roles with full school ERP access (nav + manage bypass). */
export const FULL_SCHOOL_NAV_ROLES = ['school_admin', 'super_admin'];

export function isSchoolObserver(user) {
  return user?.role_slug === 'super_admin' && !!user?.school_observer_mode;
}

function isManagePermission(permission) {
  const p = String(permission || '');
  return p.startsWith('manage_') || p === 'approve_leave';
}

/** Per-feature view vs manage permission keys (must match backend FeaturePermission). */
export const FEATURE_CAPS = {
  exams: {
    view: ['view_exams', 'view_own_data', 'view_child_data'],
    manage: ['manage_exams'],
    navPath: '/exams',
  },
  fees: {
    view: ['view_fees', 'view_own_data', 'view_child_data'],
    manage: ['manage_fees'],
    navPath: '/fees/management',
  },
  students: {
    view: ['view_students', 'view_child_data', 'view_own_data'],
    manage: ['manage_students'],
    navPath: '/students',
  },
  teachers: {
    view: ['manage_teachers'],
    manage: ['manage_teachers'],
    navPath: '/hr/teachers',
  },
  teacher_attendance: {
    view: ['manage_teacher_attendance', 'manage_teachers'],
    manage: ['manage_teacher_attendance', 'manage_teachers'],
    navPath: '/teachers/attendance',
  },
  library: {
    view: ['view_assignments', 'manage_library'],
    manage: ['manage_library'],
    navPath: '/library',
  },
  transport: {
    view: ['manage_transport'],
    manage: ['manage_transport'],
    navPath: '/transport',
  },
  attendance: {
    view: ['view_attendance', 'manage_attendance'],
    manage: ['manage_attendance'],
    navPath: '/attendance',
  },
  subjects: {
    view: ['view_school', 'manage_academics', 'manage_subjects'],
    manage: ['manage_academics', 'manage_subjects'],
    navPath: '/subjects',
  },
  classes: {
    view: ['view_school', 'manage_academics', 'manage_class_teachers'],
    manage: ['manage_academics'],
    navPath: '/classes',
  },
  timetable: {
    view: ['view_school', 'manage_academics'],
    manage: ['manage_academics'],
    navPath: '/timetable',
  },
  events: {
    view: ['view_school', 'manage_academics'],
    manage: ['manage_academics'],
    navPath: '/events',
  },
  assignments: {
    view: ['view_assignments', 'manage_assignments'],
    manage: ['manage_assignments'],
    navPath: '/assignments',
  },
  announcements: {
    view: ['view_school', 'manage_academics'],
    manage: ['manage_academics'],
    navPath: '/announcements',
  },
  reports: {
    view: ['view_reports'],
    manage: ['manage_reports'],
    navPath: '/reports/report-card',
  },
  leave: {
    view: ['approve_leave', 'manage_leave', 'apply_leave', 'view_own_data', 'view_child_data'],
    manage: ['manage_leave'],
    navPath: '/leave',
  },
  reception: {
    view: ['view_reception', 'manage_reception'],
    manage: ['manage_reception'],
    navPath: '/reception',
  },
  portal: {
    view: ['view_own_data', 'view_child_data', 'view_portal_calendar', 'view_portal_messages'],
    manage: [],
  },
  settings: {
    view: ['manage_school'],
    manage: ['manage_school', 'manage_users'],
    navPath: '/settings',
  },
};

function normalizePermArray(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => String(p).trim()).filter(Boolean);
}

export function getEffectivePermissions(user) {
  const fromApi = normalizePermArray(user?.effective_permissions);
  if (fromApi.length > 0) return fromApi;

  const rolePerms = normalizePermArray(user?.role_permissions);
  const extraPerms = normalizePermArray(user?.extra_permissions);
  if (rolePerms.length === 0 && extraPerms.length === 0) return [];
  return [...new Set([...rolePerms, ...extraPerms])];
}

export function hasFullSchoolNav(user) {
  const role = user?.role_slug;
  return FULL_SCHOOL_NAV_ROLES.includes(role);
}

export function hasPermission(user, permission) {
  if (!permission) return false;
  if (isSchoolObserver(user) && isManagePermission(permission)) return false;
  if (hasFullSchoolNav(user)) return true;
  return getEffectivePermissions(user).includes(permission);
}

/** All permission keys that grant nav access per path (view ∪ manage, deduped). */
export function navPermissionsForPath(path) {
  const keys = new Set();
  for (const cap of Object.values(FEATURE_CAPS)) {
    if (cap.navPath === path) {
      (cap.view || []).forEach((k) => keys.add(k));
      (cap.manage || []).forEach((k) => keys.add(k));
    }
  }
  return [...keys];
}

/** Build NAV_PATH_PERMISSIONS map from FEATURE_CAPS. */
export function buildNavPathPermissions() {
  const map = {};
  for (const cap of Object.values(FEATURE_CAPS)) {
    if (!cap.navPath) continue;
    map[cap.navPath] = navPermissionsForPath(cap.navPath);
  }
  return map;
}

function hasAnyPermission(user, keys) {
  if (!keys?.length) return false;
  if (hasFullSchoolNav(user)) return true;
  const effective = getEffectivePermissions(user);
  return keys.some((k) => effective.includes(k));
}

export function canViewFeature(user, featureKey) {
  const cap = FEATURE_CAPS[featureKey];
  if (!cap) return false;
  if (hasFullSchoolNav(user)) return true;
  if (hasAnyPermission(user, cap.manage)) return true;
  return hasAnyPermission(user, cap.view);
}

export function canManageFeature(user, featureKey) {
  if (isSchoolObserver(user)) return false;
  const cap = FEATURE_CAPS[featureKey];
  if (!cap) return false;
  if (hasFullSchoolNav(user)) return true;
  return hasAnyPermission(user, cap.manage);
}

export function canAssignClassTeachers(user) {
  if (isSchoolObserver(user)) return false;
  if (hasFullSchoolNav(user)) return true;
  return hasPermission(user, 'manage_class_teachers');
}
